import { useEffect, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { isEmpty } from 'lodash'
import classNames from 'classnames'
import { Button, Textarea } from '@collegebacker/backer-ui/ui'

import { track } from 'utils'
import { hasActivePaymentMethod } from 'utils/user'
import { Step, User } from '../../types'
import {
  bankAccountValue,
  creditCardValue,
  messageValue
} from 'modules/contribution/LocalState'
import { CurrentFlow } from 'modules/contribution/types'
import { buildPath } from 'routes'
import { isMobile, NO_FUND_HANDLE } from 'utils/helpers'
import {
  getPaymentMethod,
  isStarterGiftFlow
} from 'modules/contribution/helpers'

import styles from './GiftingMessage.module.scss'

const GiftingMessage = ({
  dispatch,
  user,
  redirectToContributionFirstStepIfReload
}: {
  dispatch: any
  user: User
  redirectToContributionFirstStepIfReload?: () => void
}) => {
  const history = useHistory()
  const { flow }: { flow: CurrentFlow } = useParams()
  const futureStep = nextStep(user)
  const defaultMessage = `Hi! 👋 I hope this gift helps your kid chase after their dreams! I know they're going to do big things someday. ❤️`

  const [message, setMessage] = useState('')
  const [errors, setErrors] = useState('')
  const [skipMessage, setSkipMessage] = useState(false)

  useEffect(() => {
    if (redirectToContributionFirstStepIfReload)
      redirectToContributionFirstStepIfReload()
  }, [])

  useEffect(() => {
    if (messageValue()) {
      setMessage(messageValue())
    } else {
      setMessage(defaultMessage)
      messageValue(defaultMessage)
    }
  }, [])

  return (
    <form
      className={styles.container}
      onSubmit={(event) => {
        event.preventDefault()
        const errors: any = validate({ message })
        setErrors(errors.message)

        if (skipMessage || isEmpty(errors)) {
          track('ContributionMessage', {})

          if (isStarterGiftFlow(flow)) {
            history.push(
              buildPath('contributionFlow', {
                handle: NO_FUND_HANDLE,
                flow: 'create-starter-gift',
                step: futureStep
              })
            )
          } else {
            dispatch({
              type: 'setActiveStep',
              payload: {
                activeStep: futureStep
              }
            })
          }
        }
      }}
    >
      <div className={styles.header}>
        <h2
          className={classNames('typo-app-title-medium-adaptive', styles.title)}
        >
          Add a note
        </h2>

        <p className={classNames('typo-app-body-main', styles.subtitle)}>
          Add a personal message to go with your gift.
        </p>
      </div>
      <Textarea
        className={styles.textarea}
        label="Customize message:"
        value={message}
        errorMessage={errors}
        onChange={(value: string) => {
          setMessage(value)
          messageValue(value)
          setErrors('')
        }}
        autoFocus={!isMobile()}
        rows={6}
      />
      <Button
        className={styles.submitCta}
        mode="primary"
        label="Continue"
        type="submit"
      />
      <Button
        className={styles.submitCta}
        mode="outline"
        label="Skip"
        type="submit"
        onClick={() => {
          setSkipMessage(true)
          messageValue('')
          setMessage('')
          setErrors('')
        }}
      />
    </form>
  )
}

const nextStep: (user: User) => Step = (user) => {
  const paymentMethod = getPaymentMethod(
    user,
    bankAccountValue,
    creditCardValue
  )
  const { bankAccount, creditCard } = paymentMethod
  if (bankAccount || creditCard || hasActivePaymentMethod(user)) {
    return 'confirmation'
  } else {
    return 'add-payment-method'
  }
}

export default GiftingMessage

function validate({ message }: { message: string }) {
  const errors: any = {}
  message = message.trim()

  if (message.indexOf(`kid's name`) > 0)
    errors['message'] = `Please edit or remove [kid's name] line.`
  else if (message.indexOf(`[name]`) > 0)
    errors['message'] = `Please edit or remove [name] line.`
  else if (message.length < 1)
    errors['message'] = `Please enter a message or skip`
  return errors
}
